import React, { useState, useEffect } from 'react';
import Topbar from '../components/Topbar';
import Modal from '../components/Modal';
import api, { API } from '../utils/api';
import { MagnifyingGlass, PencilSimple, Trash } from '@phosphor-icons/react';
import { toast } from 'sonner';
import useContentHeight from '../hooks/useContentHeight';

const Clientes = () => {
  const contentHeight = useContentHeight(280);
  const [clientes, setClientes] = useState([]);
  const [filteredClientes, setFilteredClientes] = useState([]);
  const [dominios, setDominios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCliente, setEditingCliente] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('todos');
  
  const [formData, setFormData] = useState({
    nombre: '',
    contacto: '',
    email: '',
    telefono: '',
    rfc: '',
    dominio_principal: '',
    direccion: '',
    fecha_inicio: '',
    estado: 'activo',
    servicios: []
  });

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    filterClientes();
  }, [clientes, searchTerm, filter]);

  const fetchData = async () => {
    try {
      const [clientesRes, dominiosRes] = await Promise.all([
        api.get('/clientes'),
        api.get('/dominios')
      ]);
      setClientes(clientesRes.data);
      setDominios(dominiosRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const filterClientes = () => {
    let filtered = [...clientes];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(c =>
        c.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.contacto.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Status filter
    if (filter === 'activos') {
      filtered = filtered.filter(c => c.estado === 'activo');
    } else if (filter === 'por_vencer') {
      filtered = filtered.filter(c => c.servicios.length > 0);
    }

    // Ordenar alfabéticamente por nombre
    filtered.sort((a, b) => a.nombre.localeCompare(b.nombre));

    setFilteredClientes(filtered);
  };

  const handleAdd = () => {
    setEditingCliente(null);
    setFormData({
      nombre: '',
      contacto: '',
      email: '',
      telefono: '',
      rfc: '',
      dominio_principal: '',
      direccion: '',
      fecha_inicio: new Date().toISOString().split('T')[0],
      estado: 'activo',
      servicios: []
    });
    setShowModal(true);
  };

  const handleEdit = (cliente) => {
    setEditingCliente(cliente);
    setFormData(cliente);
    setShowModal(true);
  };

  const handleDelete = async (clienteId) => {
    if (!window.confirm('¿Estás seguro de eliminar este cliente?')) return;

    try {
      await api.delete(`/clientes/${clienteId}`);
      toast.success('Cliente eliminado exitosamente');
      fetchData();
    } catch (error) {
      console.error('Error deleting cliente:', error);
      toast.error('Error al eliminar cliente');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingCliente) {
        await api.put(`/clientes/${editingCliente.id}`, formData);
        toast.success('Cliente actualizado exitosamente');
      } else {
        await api.post('/clientes', formData);
        toast.success('Cliente creado exitosamente');
      }
      setShowModal(false);
      fetchData();
    } catch (error) {
      console.error('Error saving cliente:', error);
      toast.error('Error al guardar cliente');
    }
  };

  const handleExport = async () => {
    try {
      const token = localStorage.getItem('token');
      window.open(`${API}/export/clientes?token=${token}`, '_blank');
      toast.success('Exportando clientes...');
    } catch (error) {
      toast.error('Error al exportar');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-[#0d0f14]">
        <div className="text-[#4f9eff] font-['IBM_Plex_Mono'] text-lg">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto bg-[#0d0f14] min-h-screen">
      <Topbar
        title="Clientes"
        breadcrumb="Visionweb / Módulos / Clientes"
        onAdd={handleAdd}
        onExport={handleExport}
        showAdd={true}
        showExport={true}
      />

      <div className="p-4 md:p-6 pb-12">
        {/* Toolbar */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 md:gap-4 mb-4 md:mb-6" data-testid="toolbar">
          <div className="flex-1 relative">
            <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8b949e]" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar clientes..."
              className="w-full bg-[#13161e] border border-[#2a2e3d] rounded-sm pl-10 pr-4 py-2 text-white font-['Mulish'] text-sm focus:border-[#4f9eff] focus:outline-none transition-colors"
              data-testid="search-input"
            />
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1 sm:pb-0">
            {['todos', 'activos', 'por_vencer'].map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-3 md:px-4 py-2 rounded-sm font-['IBM_Plex_Mono'] text-xs whitespace-nowrap transition-colors ${
                  filter === f
                    ? 'bg-[#4f9eff] text-[#0d0f14] font-bold'
                    : 'bg-[#13161e] border border-[#2a2e3d] text-[#8b949e] hover:text-white'
                }`}
                data-testid={`filter-${f}`}
              >
                {f === 'todos' ? 'Todos' : f === 'activos' ? 'Activos' : 'Por vencer'}
              </button>
            ))}
          </div>
        </div>

        {/* Mobile Cards View */}
        <div className="block md:hidden overflow-y-auto space-y-3 pr-1" style={{ maxHeight: contentHeight }}>
          {filteredClientes.map((cliente) => (
            <div key={cliente.id} className="bg-[#13161e] border border-[#2a2e3d] rounded-md p-4" data-testid={`cliente-card-${cliente.id}`}>
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1 min-w-0">
                  <div className="text-white font-['Mulish'] text-sm font-semibold truncate">{cliente.nombre}</div>
                  <div className="text-[#8b949e] font-['Mulish'] text-xs truncate">{cliente.contacto}</div>
                </div>
                <span className={`inline-flex items-center px-2 py-1 border rounded-sm font-['IBM_Plex_Mono'] text-[10px] ml-2 ${
                  cliente.estado === 'activo' ? 'bg-[#00d68f]/10 border-[#00d68f] text-[#00d68f]' :
                  cliente.estado === 'inactivo' ? 'bg-[#8b949e]/10 border-[#8b949e] text-[#8b949e]' :
                  'bg-[#ffc84a]/10 border-[#ffc84a] text-[#ffc84a]'
                }`}>
                  {cliente.estado}
                </span>
              </div>
              <div className="flex items-center justify-between text-xs mb-2">
                <span className="text-[#4f9eff] font-['IBM_Plex_Mono'] truncate">{cliente.dominio_principal || '-'}</span>
                <span className="text-[#8b949e] font-['IBM_Plex_Mono']">{cliente.fecha_inicio}</span>
              </div>
              <div className="flex items-center gap-2 mt-3 pt-3 border-t border-[#2a2e3d]">
                <button
                  onClick={() => handleEdit(cliente)}
                  className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-[#4f9eff]/10 border border-[#4f9eff] text-[#4f9eff] rounded-sm text-xs font-['IBM_Plex_Mono']"
                  data-testid={`edit-cliente-${cliente.id}`}
                >
                  <PencilSimple size={14} /> Editar
                </button>
                <button
                  onClick={() => handleDelete(cliente.id)}
                  className="px-3 py-2 bg-[#ff5252]/10 border border-[#ff5252] text-[#ff5252] rounded-sm"
                  data-testid={`delete-cliente-${cliente.id}`}
                >
                  <Trash size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Desktop Table */}
        <div className="hidden md:block bg-[#13161e] border border-[#2a2e3d] rounded-md overflow-hidden" data-testid="clientes-table">
          <div className="overflow-x-auto overflow-y-auto" style={{ maxHeight: 'calc(100vh - 250px)' }}>
            <table className="w-full">
              <thead className="bg-[#0d0f14] border-b border-[#2a2e3d] sticky top-0">
                <tr>
                  <th className="px-6 py-3 text-left text-[#8b949e] font-['IBM_Plex_Mono'] text-xs uppercase tracking-wider">Cliente</th>
                  <th className="px-6 py-3 text-left text-[#8b949e] font-['IBM_Plex_Mono'] text-xs uppercase tracking-wider">Contacto</th>
                  <th className="px-6 py-3 text-left text-[#8b949e] font-['IBM_Plex_Mono'] text-xs uppercase tracking-wider">Dominio</th>
                  <th className="px-6 py-3 text-left text-[#8b949e] font-['IBM_Plex_Mono'] text-xs uppercase tracking-wider whitespace-nowrap">Fecha Inicio</th>
                  <th className="px-6 py-3 text-left text-[#8b949e] font-['IBM_Plex_Mono'] text-xs uppercase tracking-wider">Estado</th>
                  <th className="px-6 py-3 text-left text-[#8b949e] font-['IBM_Plex_Mono'] text-xs uppercase tracking-wider">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#2a2e3d]">
                {filteredClientes.map((cliente) => (
                  <tr key={cliente.id} className="hover:bg-[#1a1e28] transition-colors group" data-testid={`cliente-row-${cliente.id}`}>
                    <td className="px-6 py-4 text-white font-['Mulish'] text-sm font-semibold">{cliente.nombre}</td>
                    <td className="px-6 py-4 text-[#8b949e] font-['Mulish'] text-sm">{cliente.contacto}</td>
                    <td className="px-6 py-4 text-[#4f9eff] font-['IBM_Plex_Mono'] text-sm">{cliente.dominio_principal || '-'}</td>
                    <td className="px-6 py-4 text-[#8b949e] font-['IBM_Plex_Mono'] text-sm whitespace-nowrap">{cliente.fecha_inicio}</td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-2 py-1 border rounded-sm font-['IBM_Plex_Mono'] text-xs ${
                        cliente.estado === 'activo' ? 'bg-[#00d68f]/10 border-[#00d68f] text-[#00d68f]' :
                        cliente.estado === 'inactivo' ? 'bg-[#8b949e]/10 border-[#8b949e] text-[#8b949e]' :
                        'bg-[#ffc84a]/10 border-[#ffc84a] text-[#ffc84a]'
                      }`}>
                        {cliente.estado}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleEdit(cliente)}
                          className="text-[#4f9eff] hover:text-white transition-colors"
                          data-testid={`edit-cliente-${cliente.id}`}
                        >
                          <PencilSimple size={18} />
                        </button>
                        <button
                          onClick={() => handleDelete(cliente.id)}
                          className="text-[#ff5252] hover:text-white transition-colors"
                          data-testid={`delete-cliente-${cliente.id}`}
                        >
                          <Trash size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Empty State */}
        {filteredClientes.length === 0 && (
          <div className="text-center py-12">
            <div className="text-[#8b949e] font-['Mulish'] text-sm">No se encontraron clientes</div>
          </div>
        )}
      </div>

      {/* Modal Form */}
      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={editingCliente ? 'Editar Cliente' : 'Agregar Cliente'}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-[#8b949e] font-['IBM_Plex_Mono'] text-xs uppercase tracking-wider block mb-2">
                Nombre del Cliente *
              </label>
              <input
                type="text"
                value={formData.nombre}
                onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                className="w-full bg-[#0d0f14] border border-[#2a2e3d] rounded-sm px-4 py-2 text-white font-['Mulish'] text-sm focus:border-[#4f9eff] focus:outline-none"
                required
                data-testid="form-nombre"
              />
            </div>
            <div>
              <label className="text-[#8b949e] font-['IBM_Plex_Mono'] text-xs uppercase tracking-wider block mb-2">
                Contacto *
              </label>
              <input
                type="text"
                value={formData.contacto}
                onChange={(e) => setFormData({ ...formData, contacto: e.target.value })}
                className="w-full bg-[#0d0f14] border border-[#2a2e3d] rounded-sm px-4 py-2 text-white font-['Mulish'] text-sm focus:border-[#4f9eff] focus:outline-none"
                required
                data-testid="form-contacto"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-[#8b949e] font-['IBM_Plex_Mono'] text-xs uppercase tracking-wider block mb-2">
                Email *
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full bg-[#0d0f14] border border-[#2a2e3d] rounded-sm px-4 py-2 text-white font-['Mulish'] text-sm focus:border-[#4f9eff] focus:outline-none"
                required
                data-testid="form-email"
              />
            </div>
            <div>
              <label className="text-[#8b949e] font-['IBM_Plex_Mono'] text-xs uppercase tracking-wider block mb-2">
                Teléfono
              </label>
              <input
                type="tel"
                value={formData.telefono}
                onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                className="w-full bg-[#0d0f14] border border-[#2a2e3d] rounded-sm px-4 py-2 text-white font-['Mulish'] text-sm focus:border-[#4f9eff] focus:outline-none"
                data-testid="form-telefono"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-[#8b949e] font-['IBM_Plex_Mono'] text-xs uppercase tracking-wider block mb-2">
                RFC
              </label>
              <input
                type="text"
                value={formData.rfc}
                onChange={(e) => setFormData({ ...formData, rfc: e.target.value })}
                className="w-full bg-[#0d0f14] border border-[#2a2e3d] rounded-sm px-4 py-2 text-white font-['Mulish'] text-sm focus:border-[#4f9eff] focus:outline-none"
                data-testid="form-rfc"
              />
            </div>
            <div>
              <label className="text-[#8b949e] font-['IBM_Plex_Mono'] text-xs uppercase tracking-wider block mb-2">
                Dominio Principal
              </label>
              <select
                value={formData.dominio_principal}
                onChange={(e) => setFormData({ ...formData, dominio_principal: e.target.value })}
                className="w-full bg-[#0d0f14] border border-[#2a2e3d] rounded-sm px-4 py-2 text-white font-['Mulish'] text-sm focus:border-[#4f9eff] focus:outline-none"
                data-testid="form-dominio"
              >
                <option value="">Seleccionar dominio (opcional)</option>
                {[...dominios].sort((a, b) => a.nombre.localeCompare(b.nombre)).map(dominio => (
                  <option key={dominio.id} value={dominio.nombre}>{dominio.nombre}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="text-[#8b949e] font-['IBM_Plex_Mono'] text-xs uppercase tracking-wider block mb-2">
              Dirección
            </label>
            <input
              type="text"
              value={formData.direccion}
              onChange={(e) => setFormData({ ...formData, direccion: e.target.value })}
              className="w-full bg-[#0d0f14] border border-[#2a2e3d] rounded-sm px-4 py-2 text-white font-['Mulish'] text-sm focus:border-[#4f9eff] focus:outline-none"
              data-testid="form-direccion"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-[#8b949e] font-['IBM_Plex_Mono'] text-xs uppercase tracking-wider block mb-2">
                Fecha de Inicio *
              </label>
              <input
                type="date"
                value={formData.fecha_inicio}
                onChange={(e) => setFormData({ ...formData, fecha_inicio: e.target.value })}
                className="w-full bg-[#0d0f14] border border-[#2a2e3d] rounded-sm px-4 py-2 text-white font-['Mulish'] text-sm focus:border-[#4f9eff] focus:outline-none"
                required
                data-testid="form-fecha-inicio"
              />
            </div>
            <div>
              <label className="text-[#8b949e] font-['IBM_Plex_Mono'] text-xs uppercase tracking-wider block mb-2">
                Estado
              </label>
              <select
                value={formData.estado}
                onChange={(e) => setFormData({ ...formData, estado: e.target.value })}
                className="w-full bg-[#0d0f14] border border-[#2a2e3d] rounded-sm px-4 py-2 text-white font-['Mulish'] text-sm focus:border-[#4f9eff] focus:outline-none"
                data-testid="form-estado"
              >
                <option value="activo">Activo</option>
                <option value="inactivo">Inactivo</option>
                <option value="suspendido">Suspendido</option>
              </select>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={() => setShowModal(false)}
              className="px-4 py-2 bg-transparent border border-[#2a2e3d] text-white rounded-sm hover:border-[#4f9eff] transition-colors font-['IBM_Plex_Mono'] text-sm"
              data-testid="form-cancel"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-[#4f9eff] text-[#0d0f14] rounded-sm hover:bg-[#4f9eff]/90 transition-colors font-['IBM_Plex_Mono'] text-sm font-bold"
              data-testid="form-submit"
            >
              Guardar
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Clientes;
